import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { categoryAPI, serviceAPI } from '../utils/api';
import ServiceCard from '../components/common/ServiceCard';
import './Home.css';

const CATEGORIES = [
  { slug: 'food-catering', icon: '🍛', label: 'Food & Catering', desc: 'Authentic cuisines & catering', color: '#FEF3C7', accent: '#D97706' },
  { slug: 'hair-beauty', icon: '💇', label: 'Hair & Beauty', desc: 'Braiding, locs & beauty', color: '#FCE7F3', accent: '#DB2777' },
  { slug: 'tailoring', icon: '🧵', label: 'Tailoring', desc: 'Custom African attire', color: '#D1FAE5', accent: '#059669' },
  { slug: 'event-planning', icon: '🎉', label: 'Event Planning', desc: 'Celebrations & weddings', color: '#EDE9FE', accent: '#7C3AED' },
];

export default function Home() {
  const [search, setSearch] = useState('');
  const [featured, setFeatured] = useState([]);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const load = async () => {
      try {
        const [featuredRes, recentRes] = await Promise.all([
          serviceAPI.search({ featured: true, limit: 4 }),
          serviceAPI.search({ limit: 8, sortBy: 'createdAt', sortOrder: 'desc' }),
        ]);
        setFeatured(featuredRes.data.services);
        setRecent(recentRes.data.services);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (search.trim()) navigate(`/browse?search=${encodeURIComponent(search.trim())}`);
  };

  return (
    <div className="home">
      {/* Hero */}
      <section className="hero">
        <div className="hero-overlay" />
        <div className="hero-content container">
          <div className="hero-badge">🌍 African Community Marketplace</div>
          <h1 className="hero-title">
            Find Trusted African<br />
            <span className="hero-highlight">Service Providers</span><br />
            Near You
          </h1>
          <p className="hero-subtitle">
            Connect with skilled African professionals for food, beauty, tailoring,
            event planning, and more — all in one place.
          </p>
          <form onSubmit={handleSearch} className="hero-search">
            <div className="search-bar hero-search-bar">
              <input
                type="text"
                placeholder="Search for food catering, braiding, tailoring..."
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
              <button type="submit">Search</button>
            </div>
          </form>
          <div className="hero-tags">
            <span>Popular:</span>
            {['Jollof Rice', 'Box Braids', 'Ankara Dress', 'Wedding Planning'].map(t => (
              <button key={t} onClick={() => navigate(`/browse?search=${encodeURIComponent(t)}`)} className="hero-tag">
                {t}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="stats-bar">
        <div className="container stats-inner">
          {[
            { num: '500+', label: 'Service Providers', icon: '🧑‍💼' },
            { num: '2,000+', label: 'Services Listed', icon: '📋' },
            { num: '10,000+', label: 'Happy Customers', icon: '😊' },
            { num: '4.8★', label: 'Average Rating', icon: '⭐' },
          ].map(s => (
            <div key={s.label} className="stat-item">
              <span className="stat-icon">{s.icon}</span>
              <span className="stat-num">{s.num}</span>
              <span className="stat-label">{s.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Categories */}
      <section className="section container">
        <div className="section-header">
          <h2>Browse by Category</h2>
          <Link to="/browse" className="see-all">See All →</Link>
        </div>
        <div className="categories-grid">
          {CATEGORIES.map(cat => (
            <Link to={`/browse?category=${cat.slug}`} key={cat.slug} className="category-card">
              <div className="category-icon-wrap" style={{ background: cat.color }}>
                <span className="category-icon">{cat.icon}</span>
              </div>
              <div>
                <div className="category-name">{cat.label}</div>
                <div className="category-desc">{cat.desc}</div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Featured Services */}
      {featured.length > 0 && (
        <section className="section section-bg">
          <div className="container">
            <div className="section-header">
              <h2>⭐ Featured Services</h2>
              <Link to="/browse?featured=true" className="see-all">View All →</Link>
            </div>
            <div className="grid grid-4">
              {featured.map(s => <ServiceCard key={s.id} service={s} />)}
            </div>
          </div>
        </section>
      )}

      {/* Recent Services */}
      <section className="section container">
        <div className="section-header">
          <h2>🆕 Recently Listed</h2>
          <Link to="/browse" className="see-all">Browse All →</Link>
        </div>
        {loading ? (
          <div className="loading-page"><div className="spinner" /></div>
        ) : recent.length > 0 ? (
          <div className="grid grid-4">
            {recent.map(s => <ServiceCard key={s.id} service={s} />)}
          </div>
        ) : (
          <div className="empty-state">
            <div className="empty-state-icon">🔍</div>
            <h3>No services yet</h3>
            <p>Be the first to list a service!</p>
            <Link to="/register?role=seller" className="btn btn-primary">Become a Seller</Link>
          </div>
        )}
      </section>

      {/* How it works */}
      <section className="section section-bg">
        <div className="container">
          <h2 className="text-center mb-3">How It Works</h2>
          <div className="how-grid">
            {[
              { icon: '🔍', step: '1', title: 'Browse & Search', desc: 'Search for services by category, location, or keyword. Read reviews and compare providers.' },
              { icon: '📋', step: '2', title: 'Book & Pay Securely', desc: 'Book the service you need and pay securely through our Stripe-powered checkout.' },
              { icon: '⭐', step: '3', title: 'Get It Done & Review', desc: 'Receive your service and leave a review to help the community grow.' },
            ].map(h => (
              <div key={h.step} className="how-card">
                <div className="how-step">{h.step}</div>
                <div className="how-icon">{h.icon}</div>
                <h3>{h.title}</h3>
                <p>{h.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Seller CTA */}
      <section className="section container">
        <div className="seller-cta">
          <div className="seller-cta-text">
            <h2>Are You a Service Provider?</h2>
            <p>Join thousands of African professionals already on the platform. List your services, reach more customers, and grow your business.</p>
            <div className="seller-cta-perks">
              {['✅ Free to join', '✅ Secure payments', '✅ Reach thousands of customers', '✅ Grow your brand'].map(p => (
                <span key={p}>{p}</span>
              ))}
            </div>
            <div className="flex gap-1">
              <Link to="/register?role=seller" className="btn btn-primary btn-lg">Start Selling Today</Link>
              <Link to="/browse" className="btn btn-ghost btn-lg">Browse Services</Link>
            </div>
          </div>
          <div className="seller-cta-emoji">🏪</div>
        </div>
      </section>
    </div>
  );
}
